import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";
import api from "../../lib/api";

/**
 * Owner-only status chip for the Playwright anti-bot solver.
 *
 * If ready=False, DDoS-Guard / Cloudflare protected customer sites will
 * silently under-report during scans (that's how we ended up with "2 donation
 * forms watched" for a customer that actually has 88). The scheduler already
 * refuses to run sweeps in that state, so the surface-visible impact is
 * minimal — but we want the owner to SEE the health at a glance.
 *
 * Polls once on mount + on demand. Cheap endpoint (just reads a module flag).
 */
export function BrowserHealthChip() {
  const [state, setState] = useState({ loading: true, ready: null, at: "", err: "" });

  const refresh = () => {
    setState((s) => ({ ...s, loading: true }));
    api.get("/admin/browser-health").then(({ data }) => {
      setState({
        loading: false,
        ready: !!data.ready,
        at: data.last_probe_at || "",
        err: data.last_probe_error || "",
      });
    }).catch(() => setState({ loading: false, ready: null, at: "", err: "unreachable" }));
  };

  useEffect(() => { refresh(); }, []);

  const ready = state.ready === true;
  const Icon = ready ? ShieldCheck : ShieldAlert;

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${
        ready
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-red-200 bg-red-50 text-red-800"
      }`}
      title={state.err ? `Last probe error: ${state.err}` : "Playwright anti-bot solver status"}
      data-testid="browser-health-chip"
    >
      <Icon className="w-3.5 h-3.5" />
      <span>
        Browser solver: <span className="font-semibold">{ready ? "healthy" : "not ready"}</span>
      </span>
      <button
        onClick={refresh}
        className="ml-1 opacity-60 hover:opacity-100"
        aria-label="Re-check browser health"
        data-testid="browser-health-refresh"
      >
        <RefreshCw className={`w-3 h-3 ${state.loading ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
}
